import { Dayjs } from 'dayjs';

import { EVENT_STATUS_ENUM } from 'services/events/evetns.api.type';

import {
  PROMOTER_DEFAULT_SETTING_TYPE_ENUM,
  PaymentGatewayType,
  PromoterDefaultSettingType,
} from '../core/systemTypes';

export type PromoterFullInfoType = {
  paymentGatewaySettings: PaymentGatewayType[];
  defaultSettings?: PromoterDefaultSettingType[];
  billingAddressSameAsContact: boolean;
  id: string;
  name: string;
  about: string;
  url: string;
  email: string;
  mobileNumber: string;
  telephoneNumber: string;
  address: string;
  town: string;
  postcode: string;
  currency?: string;
  country: string;
  iframeStyles: string;
  ibanNumber: string;
  bankHolderName: string;
  billingName: string;
  billingAddress: string;
  billingTown: string;
  billingCountry: string;
  billingPostcode: string;
  billingVATNumber: string;
  canEdit: boolean;
  logo: string;
};

export type ReferrerLinkReqType = {
  email: string;
  promoterId: string;
  referrerLink: string;
  groupIds?: string[];
};

export type ReferrerEditLinkReqType = {
  email: string;
  promoterId: string;
  referrerLink: string;
  groupIds: string[];
  id: string;
};

export type ReferralLinkResType = {
  referrerLink: string;
  qtyOfTicketsReferred: number;
  valueOfTicketsReferred: number;
  currency: string;
  id: string;
  user: {
    firstName: string;
    lastName: string;
    userEmail: string;
    userId: string;
    profilePic: string;
  };
  promoter: {
    promoterId: string;
    promoterName: string;
  };
  metrics: {
    qtyOfTicketsReferred: number;
    valueOfTicketsReferred: number;
    currency: string;
    eventName: string;
  }[];
  groups: {
    name: string;
    id: string;
    colour: string;
  }[];
};

export type ReferrerResType = {
  nextPage: number;
  previousPage: number;
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  totalRecords: number;
  totalRecordsInPage: number;
  referrers: Referrer[];
};

export type ReferrerReqType = {
  PromoterId: string;
  Term?: string;
  Type?: ReferrerEnum;
  PageNumber: number;
  PageSize: number;
};

export enum ReferrerEnum {
  ALL = 'All',
  MOST_SALES = 'MostSales',
}

export type Referrer = {
  referrerLink: string;
  qtyOfTicketsReferred: number;
  valueOfTicketsReferred: number;
  currency: string;
  id: string; // UUID format
  user: {
    firstName: string;
    lastName: string;
    userEmail: string;
    userId: string; // UUID format
    profilePic: string;
  };
  promoter: {
    promoterId: string; // UUID format
    promoterName: string;
  };
};

export type ReferrerGroupReqType = {
  name: string;
  promoterId: string;
  colour: string;
};

export type ReferrerGroupEditReqType = {
  name: string;
  promoterId: string;
  colour: string;
  id: string;
};

export type ReferrerGroupResType = {
  name: string;
  id: string;
  colour: string;
  promoter: {
    promoterId: string;
    promoterName: string;
  };
};

export type ReferrerFilterType = {
  PageNumber?: number;
  PageSize?: number;
  Term?: string;
  Type: ReferrerEnum;
};

export type ReferrerItemResType = {
  referrerLink: string;
  qtyOfTicketsReferred: number;
  valueOfTicketsReferred: number;
  currency: string;
  user: {
    firstName: string;
    lastName: string;
    userEmail: string;
    userId: string; // UUID format
    profilePic: string;
  };
  promoter: {
    promoterId: string; // UUID format
    promoterName: string;
  };
  metrics: {
    qtyOfTicketsReferred: number;
    valueOfTicketsReferred: number;
    currency: string;
    eventName: string;
  }[];
  groups: {
    name: string;
    id: string; // UUID format
    colour: string;
  }[];
};

export enum OfferType {
  perDifferentShowNames = 'perDifferentShowNames',
  perMultipleNumberOfTickets = 'perMultipleNumberOfTickets',
  fixedDiscount = 'fixedDiscount',
  greaterOrEqualToNumberOfTickets = 'greaterOrEqualToNumberOfTickets',
  fixedPriceOnAllTicketTypes = 'fixedPriceOnAllTicketTypes',
  fixedPriceOnSpecificTicketTypes = 'fixedPriceOnSpecificTicketTypes',
  perDifferentEvents = 'perDifferentEvents',
}

export type EventOffer = {
  name: string;
  eventId: string;
  description: string;
  dateFrom: string;
  dateTo: string;
  active: boolean;
  numberOfTicketsAvailable: number;
  fixedDiscount: number;
  numberOfTicketsToGetDiscount: number;
  numberOfTicketsToGetDiscountOn: number;
  fixedDiscountValueType: 'percentage' | 'fixedValue';
  minimumNumberOfShows: number;
  offerType: OfferType;
  id: string;
  ticketTypes: {
    priceToBeSold?: number;
    ticketTypeId?: string;
    minimumNumberOfTicketsToBeBought?: number;
    id?: string;
    ticketType: {
      name: string;
      id: string;
    };
  }[];
  restrictToShows: {
    date: string;
    showId: string;
    showTime: string;
  }[];
  eventLinks: {
    eventName: string;
    eventId: string;
  }[];
};

export type EventOfferResType = {
  eventFlyer: string;
  eventName: string;
  eventId: string;
  currency: string;
  timeZone: string;
  eventStatus: EVENT_STATUS_ENUM;
  lastShowDate: string;
  scheduleType: 'nonRecurring' | 'onRecurringSchedule' | 'openDateSchedule' | 'noDate';
  promoter: {
    promoterId: string;
    name: string;
    slug: string;
    showsHappeningURL: string;
    email: string;
  };
  url: string;
  offers: EventOffer[];
};

export type EventSingleOfferResType = {
  eventFlyer: string;
  eventName: string;
  eventId: string;
  currency: string;
  timeZone: string;
  eventStatus: EVENT_STATUS_ENUM;
  lastShowDate: string;
  scheduleType: 'nonRecurring' | 'onRecurringSchedule' | 'openDateSchedule' | 'noDate';
  promoter: {
    promoterId: string;
    name: string;
    slug: string;
    showsHappeningURL: string;
    email: string;
  };
  url: string;
  offer: EventOffer;
};

export type EventOfferCreateReqType = {
  id?: string;
  name: string;
  eventId: string;
  description: string;
  dateFrom?: string; // ISO timestamp
  dateTo?: string;
  active?: boolean;
  numberOfTicketsAvailable?: number;
  fixedDiscount?: number;
  numberOfTicketsToGetDiscount?: number;
  numberOfTicketsToGetDiscountOn?: number;
  fixedDiscountValueType?: 'percentage' | 'fixedValue';
  minimumNumberOfShows?: number;
  offerType: OfferType;
  showRestrictions?: {
    showId: string;
  }[];
  eventLinks?: {
    eventId: string;
  }[];
  ticketTypes?: {
    ticketTypeId: string;
    newPrice?: number;
    minimumNumberOfTickets?: number;
  }[];
};

export enum HookType {
  notifyUserOfOrder = 'notifyUserOfOrder',
  customEmail = 'customEmail',
  saveContact = 'saveContact',
}

export enum HookSettingType {
  EmailToNotify = 'emailToNotify',
  EmailTemplate = 'emailTemplate',
  EmailSubject = 'emailSubject',
  ContactProvider = 'contactProvider',
  FieldsToSave = 'fieldsToSave ',
}

export type WebHookResType = {
  eventFlyer: string;
  eventName: string;
  eventId: string;
  currency: string;
  timeZone: string;
  eventStatus: EVENT_STATUS_ENUM;
  lastShowDate: string;
  scheduleType: 'nonRecurring' | 'onRecurringSchedule' | 'openDateSchedule' | 'noDate';
  promoter: {
    promoterId: string;
    name: string;
    slug: string;
    showsHappeningURL: string;
    email: string;
  };
  url: string;
  webhooks: WebHook[];
};

export type WebHookSingleResType = {
  eventFlyer: string;
  eventName: string;
  eventId: string;
  currency: string;
  timeZone: string;
  eventStatus: EVENT_STATUS_ENUM;
  lastShowDate: string;
  scheduleType: 'nonRecurring' | 'onRecurringSchedule' | 'openDateSchedule' | 'noDate';
  promoter: {
    promoterId: string;
    name: string;
    slug: string;
    showsHappeningURL: string;
    email: string;
  };
  url: string;
  webhook: WebHook;
};

export type WebHook = {
  description: string;
  active: boolean;
  webHookType: HookType; // Add more specific types if known
  id: string;
  ticketTypes: {
    name: string;
    id: string;
  }[];
  settings: {
    value: string;
    type: string; // Add more setting types if needed
  }[];
};

export type WebHookCreateReqType = {
  description: string;
  active: boolean;
  webHookType: HookType; // Extend as needed
  eventId: string; // UUID
  ticketTypes: {
    ticketTypeId: string; // UUID
  }[];
  settings: {
    type: string; // Extend as needed
    value: string;
  }[];
};

export interface PromoterSettingItem {
  type: PROMOTER_DEFAULT_SETTING_TYPE_ENUM;
  value: string | number;
}

export type EventScheduleReqType = {
  name: string;
  active: boolean;
  dateFrom: Dayjs; // ISO date string
  dateTo: Dayjs; // ISO date string
  eventId: string; // UUID
  days: Day[];
  id?: string;
};

export type TimeSlot = {
  time: string;
};

export type Day = {
  dayOfWeek: string; // e.g., "Sunday", "Monday", etc.
  timeslots: TimeSlot[];
};

export type EventScheduleResType = {
  eventFlyer: string;
  eventName: string;
  eventId: string;
  currency: string;
  timeZone: string;
  eventStatus: EVENT_STATUS_ENUM;
  lastShowDate: string;
  scheduleType: 'nonRecurring' | 'onRecurringSchedule' | 'openDateSchedule' | 'noDate';
  promoter: {
    promoterId: string;
    name: string;
    slug: string;
    showsHappeningURL: string;
    email: string;
  };
  url: string;
  schedules: EventScheduleReqType[];
};

export type EventSingleScheduleResType = {
  eventFlyer: string;
  eventName: string;
  eventId: string;
  currency: string;
  timeZone: string;
  eventStatus: EVENT_STATUS_ENUM;
  lastShowDate: string;
  scheduleType: 'nonRecurring' | 'onRecurringSchedule' | 'openDateSchedule' | 'noDate';
  promoter: {
    promoterId: string;
    name: string;
    slug: string;
    showsHappeningURL: string;
    email: string;
  };
  url: string;
  schedule: EventScheduleReqType;
};
